import {
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';

import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';

import { t } from 'i18next';

import {
  ADMIN_ROLE,
  STATUS_PENDING,
} from '../assets/AppConstants';

import API from '../interceptors/Api';
import APIExport from '../interceptors/ApiExport';

import { useAppContext } from '../contexts/app';
import { useUserContext } from '../contexts/user';
import {
  getUserWorkspacesByClient,
} from '../services/UserWorkspaces';

import { updateWorkspace } from '../services/Workspaces';
import { getHomeFromRole } from '../utils/routes';
import { getAllTasks, getTasks } from '../services/Tasks';
import { sessionsCheck } from '../services/Sessions';
import { getAllLinks } from '../services/Links';
import { getClientProfile } from '../services/Users';
import { useDebouncedDeepCompareEffect } from './useDebouncedEffect';
import {
  isSameWorkspaceScope,
  readLastWorkspaceScope,
  saveLastWorkspaceScope,
  workspaceScopeHeaders,
  workspaceScopeOwner,
} from './workspaceScope';

const loadWorkspaceScopedData = (headers) => Promise.all([
  getTasks(undefined, { headers }),
  getAllLinks({}, { headers }),
  getAllTasks(undefined, { headers }),
  getClientProfile().catch(() => ({ requiresOnboarding: false })),
]);

const applyWorkspaceScopeHeaders = (headers) => {
  Object.assign(API.defaults.headers.common, headers);
  Object.assign(APIExport.defaults.headers.common, headers);
};

const emptyWorkspace = {
  currentUser: {
    role: null,
  },
  id: null,
};

export const useDefaultWorkspace = () => {
  const location = useLocation();
  const { workspace } = useParams();
  const {
    handleAlert,
    blockScreen,
  } = useAppContext();
  const {
    userSession,
    getUserStatus,
    userIsLogged,
    handleChangeClientId,
  } = useUserContext();

  const [searchParams] = useSearchParams();
  const navigateRedirect = useNavigate();

  const [reload, setReload] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [openEmptyWorkspace, setOpenEmptyWorkspace] = useState(false);
  const [defaultWorkspace, setDefaultWorkspace] = useState(emptyWorkspace);

  const [isOnboarding, setIsOnboarding] = useState(false);
  const [onboardingCheck, setOnboardingCheck] = useState<any>(null);
  const workspacesRequestedWithSessionCheck = useRef<any>(null);
  const scopedDataRequestedWithSessionCheck = useRef<any>(null);

  const reloadClient = useRef<any>(false);

  const getCallBackOrDefaulUrl = ({ workspaceIndex, role }) => {
    const callbackUrl = searchParams.get('callbackUrl')
      || `/${workspaceIndex}${getHomeFromRole(role)}`
      || '/';

    return callbackUrl;
  };

  const navigateDefaultClientOrFirst = (allWorkspaces) => {
    const defaultClientIndex = allWorkspaces.findIndex(({ currentUser }) => currentUser.default);
    const index = defaultClientIndex !== -1 ? defaultClientIndex : 0;
    const actualWs = allWorkspaces[index];
    const overrideRole = import.meta.env.VITE_OVERRIDE_WORKSPACE_ROLE;
    const role = overrideRole || actualWs.currentUser.role;

    navigateRedirect(
      getCallBackOrDefaulUrl({ workspaceIndex: index, role }),
      {
        replace: true,
        state: {
          fromHome: true,
        },
      },
    );
  };

  const navigateOnboarding = (index) => {
    const url = `/${index}/onboarding`;

    navigateRedirect(
      url,
      {
        state: {
          fromHome: true,
        },
      },
    );
  };

  useEffect(() => {
    const fn = async () => {
      try {
        await sessionsCheck();
        setIsAllowed(true);
      } catch (error) {
        handleAlert(error, 'error');
        setIsAllowed(false);
      }
    };

    if (userIsLogged() && getUserStatus() !== STATUS_PENDING) {
      if (!isAllowed) {
        workspacesRequestedWithSessionCheck.current = getUserWorkspacesByClient();
        workspacesRequestedWithSessionCheck.current.catch(() => {});

        const lastScope = readLastWorkspaceScope(workspaceScopeOwner(userSession), Number(workspace));
        scopedDataRequestedWithSessionCheck.current = lastScope
          ? { headers: lastScope, request: loadWorkspaceScopedData(lastScope) }
          : null;
        scopedDataRequestedWithSessionCheck.current?.request.catch(() => {});
      }
      fn();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useDebouncedDeepCompareEffect(() => {
    const loadWorkspaces = async () => {
      try {
        const workspacesRequest = workspacesRequestedWithSessionCheck.current ?? getUserWorkspacesByClient();
        workspacesRequestedWithSessionCheck.current = null;
        const workspaceList = await workspacesRequest;

        const newWorkspaces = workspaceList.filter(({ currentUser }) => currentUser.status === 'ACTIVE');

        if (reloadClient.current) {
          navigateDefaultClientOrFirst(newWorkspaces);
          reloadClient.current = false;
          window.location.reload();
        }

        setWorkspaces(newWorkspaces.map((wk) => ({ ...wk, typeName: t(wk.parentId ? 'View' : 'Workspace') })));
        setOpenEmptyWorkspace(newWorkspaces.length === 0);

        // While the URL points at /ws/<workspaceId>/..., the WorkspaceIdRedirect route owns navigation —
        // skip the default-fallback path here so we don't race it and land in workspace 0.
        if (location.pathname.startsWith('/ws/')) {
          return;
        }

        if (newWorkspaces.length > 0) {
          const index = Number(workspace);

          const isWrongWorkspace = (
            index === undefined || index === null
            || Number.isNaN(index) || index > newWorkspaces.length - 1
          );

          if (isWrongWorkspace) {
            navigateDefaultClientOrFirst(newWorkspaces);
          } else if (index < newWorkspaces.length) {
            const actualWs = newWorkspaces[index];

            const scopeHeaders = workspaceScopeHeaders(actualWs);
            applyWorkspaceScopeHeaders(scopeHeaders);

            const earlyScopedData = scopedDataRequestedWithSessionCheck.current;
            scopedDataRequestedWithSessionCheck.current = null;
            const scopedDataRequest = earlyScopedData && isSameWorkspaceScope(earlyScopedData.headers, scopeHeaders)
              ? earlyScopedData.request
              : loadWorkspaceScopedData(scopeHeaders);

            const [allEnabledTasks, links, allTasksRes, clientProfile] = await scopedDataRequest;
            saveLastWorkspaceScope(workspaceScopeOwner(userSession), index, scopeHeaders);

            setTasks(allEnabledTasks);
            setAllTasks(allTasksRes);
            setOnboardingCheck({
              links: links.content,
              requiresOnboarding: Boolean(clientProfile?.requiresOnboarding),
              checkedAt: Date.now(),
            });

            const newIsOnboarding = links.content.length === 0
              && Boolean(clientProfile?.requiresOnboarding);

            setIsOnboarding(() => newIsOnboarding);

            if (newIsOnboarding) navigateOnboarding(index);

            const overrideRole = import.meta.env.VITE_OVERRIDE_WORKSPACE_ROLE;
            const wsToSet = overrideRole
              ? { ...actualWs, currentUser: { ...actualWs.currentUser, role: overrideRole } }
              : actualWs;
            setDefaultWorkspace(wsToSet);
          }
        }
      } catch (error) {
        handleAlert(error, 'error');
      }
    };

    if (isAllowed) {
      if (userIsLogged() && getUserStatus() !== STATUS_PENDING) {
        loadWorkspaces();
      } else {
        setDefaultWorkspace(emptyWorkspace);
      }
    }
  }, [reload, userSession, workspace, isAllowed], 0);

  const handleChangeWsAndClient = (data) => {
    handleChangeClientId(data);
    reloadClient.current = true;
  };

  const handleEditWorkspace = async (data) => {
    blockScreen(true);
    try {
      await updateWorkspace(defaultWorkspace.id, { ...data, id: defaultWorkspace.id });
      handleAlert('Workspace successfully saved!', 'success');
    } catch (error) {
      handleAlert(error, 'error');
    } finally {
      blockScreen(false);
    }
  };

  const getWorkspaceRole = () => defaultWorkspace.currentUser.role;
  const isWorkspaceAdmin = () => getWorkspaceRole() === ADMIN_ROLE;

  const redirectHome = () => {
    navigateRedirect(
      getCallBackOrDefaulUrl({ workspaceIndex: workspace, role: getWorkspaceRole() }),
    );
  };

  const handleNavigateByIndex = (index) => {
    const actualWs = workspaces[index];
    navigateRedirect(
      getCallBackOrDefaulUrl({ workspaceIndex: index, role: actualWs.currentUser.role }),
    );

    window.location.reload();
  };

  const reloadWorkspaces = () => setReload(!reload);
  const handleCloseEmptyWorkspace = () => setOpenEmptyWorkspace(false);

  const value = useMemo(() => ({
    tasks,
    allTasks,
    workspaces,
    isOnboarding,
    onboardingCheck,
    defaultWorkspace,
    openEmptyWorkspace,
    redirectHome,
    setIsOnboarding,
    getWorkspaceRole,
    isWorkspaceAdmin,
    reloadWorkspaces,
    handleEditWorkspace,
    handleNavigateByIndex,
    handleChangeWsAndClient,
    handleCloseEmptyWorkspace,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [defaultWorkspace, workspaces, openEmptyWorkspace, tasks, allTasks, isOnboarding, onboardingCheck]);

  return value;
};
